import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const mobileBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target as Node) &&
        mobileBtnRef.current &&
        !mobileBtnRef.current.contains(event.target as Node)
      ) {
        setIsMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || isMobileMenuOpen
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-18">
          <div className="flex items-center gap-8">
            <a href="/" className="flex items-center gap-2.5 group">
              <div className="w-8 h-8 rounded-lg bg-foreground-900 flex items-center justify-center">
                <i className="ri-database-2-line text-background-50 text-sm"></i>
              </div>
              <span className={`font-heading text-lg font-bold tracking-tight transition-colors ${
                isScrolled || isMobileMenuOpen ? 'text-foreground-900' : 'text-background-50'
              }`}>
                KakobuyFinds
              </span>
            </a>

            <div className="hidden md:flex items-center gap-6">
              <a href="/" className={`text-sm font-medium transition-colors ${
                isScrolled ? 'text-foreground-700 hover:text-foreground-900' : 'text-background-50/80 hover:text-background-50'
              }`}>
                Home
              </a>
              <div ref={dropdownRef} className="relative">
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className={`flex items-center gap-1.5 text-sm font-medium transition-colors ${
                    isScrolled ? 'text-foreground-700 hover:text-foreground-900' : 'text-background-50/80 hover:text-background-50'
                  }`}
                >
                  Categories
                  <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {isDropdownOpen && (
                  <div className="absolute top-full mt-2 left-0 w-72 bg-background-50 rounded-xl border border-background-200/80 py-2 grid grid-cols-2 gap-0.5"
                    style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}
                  >
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        onClick={() => setIsDropdownOpen(false)}
                        className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-900 hover:bg-background-100 rounded-lg transition-colors whitespace-nowrap"
                      >
                        <span className="w-5 h-5 flex items-center justify-center">
                          <i className={`${cat.icon} text-xs`}></i>
                        </span>
                        {cat.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>
              <a href="/kakobuy-spreadsheet-guide" className={`text-sm font-medium transition-colors ${
                isScrolled ? 'text-foreground-700 hover:text-foreground-900' : 'text-background-50/80 hover:text-background-50'
              }`}>
                Guide
              </a>
              <a href="/cssbuy-spreadsheet-guide" className={`text-sm font-medium transition-colors ${
                isScrolled ? 'text-foreground-700 hover:text-foreground-900' : 'text-background-50/80 hover:text-background-50'
              }`}>
                CSSBuy
              </a>
              <a href="/superbuy-spreadsheet-guide" className={`text-sm font-medium transition-colors ${
                isScrolled ? 'text-foreground-700 hover:text-foreground-900' : 'text-background-50/80 hover:text-background-50'
              }`}>
                Superbuy
              </a>
              <a href="/oopbuy-spreadsheet-guide" className={`text-sm font-medium transition-colors ${
                isScrolled ? 'text-foreground-700 hover:text-foreground-900' : 'text-background-50/80 hover:text-background-50'
              }`}>
                Oopbuy
              </a>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="hidden md:inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-foreground-900 text-background-50 text-sm font-semibold hover:bg-foreground-800 transition-all whitespace-nowrap"
            >
              Buy It Now
              <i className="ri-arrow-right-line text-sm"></i>
            </a>
            <button
              ref={mobileBtnRef}
              onClick={() => setIsMobileMenuOpen((prev) => !prev)}
              className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
              aria-label="Toggle menu"
            >
              <i className={`text-xl transition-colors ${isScrolled || isMobileMenuOpen ? 'text-foreground-900' : 'text-background-50'} ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Panel */}
      {isMobileMenuOpen && (
        <div
          ref={mobileMenuRef}
          className="md:hidden absolute top-16 left-0 right-0 bg-background-50 border-t border-background-100 max-h-[calc(100vh-4rem)] overflow-y-auto overscroll-contain"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          <div className="px-6 pb-8 pt-2">
          <div className="flex flex-col gap-1">
            <a
              href="/"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:text-foreground-900 hover:bg-background-100 transition-colors"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-home-4-line text-sm"></i>
              </span>
              Home
            </a>
            <a
              href="/kakobuy-spreadsheet-guide"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:text-foreground-900 hover:bg-background-100 transition-colors"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-book-open-line text-sm"></i>
              </span>
              Kakobuy Guide
            </a>
            <a
              href="/cssbuy-spreadsheet-guide"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:text-foreground-900 hover:bg-background-100 transition-colors"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-article-line text-sm"></i>
              </span>
              CSSBuy Guide
            </a>
            <a
              href="/superbuy-spreadsheet-guide"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:text-foreground-900 hover:bg-background-100 transition-colors"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-article-line text-sm"></i>
              </span>
              Superbuy Guide
            </a>
            <a
              href="/oopbuy-spreadsheet-guide"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:text-foreground-900 hover:bg-background-100 transition-colors"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-article-line text-sm"></i>
              </span>
              Oopbuy Guide
            </a>

            <div className="my-2 border-t border-background-100"></div>
            <p className="px-3 py-1.5 text-xs font-semibold text-foreground-400 uppercase tracking-wider">Categories</p>

            {categories.map((cat) => (
              <a
                key={cat.name}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:text-foreground-900 hover:bg-background-100 transition-colors"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className={`${cat.icon} text-sm`}></i>
                </span>
                {cat.name}
              </a>
            ))}

            <div className="my-2 border-t border-background-100"></div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center justify-center gap-2 mx-3 mt-2 px-5 py-3 rounded-full bg-foreground-900 text-background-50 text-sm font-semibold hover:bg-foreground-800 transition-all"
            >
              Buy It Now
              <i className="ri-arrow-right-line text-sm"></i>
            </a>
          </div>
          </div>
        </div>
      )}
    </nav>
  );
}